import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import {
  useSaveDraftReading,
  useUpdateDraftReading,
  useAttachReadingPhoto,
} from '@/hooks/use-meter-readings'
import { readingPhotoUrl } from '@/lib/meter-readings'
import { roundMeterUnits, type PlotReadingStatus, type UtilityType } from '@riverpark/types'

interface MeterReadingModalProps {
  open: boolean
  onClose: () => void
  utilityType: UtilityType
  billingPeriod: string
  plotStatus: PlotReadingStatus
}

export function MeterReadingModal({
  open,
  onClose,
  utilityType,
  billingPeriod,
  plotStatus,
}: MeterReadingModalProps) {
  const save = useSaveDraftReading(utilityType.id, billingPeriod)
  const update = useUpdateDraftReading(utilityType.id, billingPeriod)
  const attachPhoto = useAttachReadingPhoto(utilityType.id, billingPeriod)

  const [previousReading, setPreviousReading] = useState('')
  const [currentReading, setCurrentReading] = useState('')
  const [notes, setNotes] = useState('')
  const [photo, setPhoto] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const existing = plotStatus.reading
  const isDraft = existing?.status === 'draft'
  const isPhotoPending = existing?.status === 'photo_pending'
  /** Posted / pending_correction — fully read-only, no action available here. */
  const isReadOnly = existing != null && !isDraft && !isPhotoPending
  const fieldsDisabled = isReadOnly

  useEffect(() => {
    if (!open) return
    setPreviousReading(String(existing?.previousReading ?? plotStatus.suggestedPreviousReading))
    setCurrentReading(existing ? String(existing.currentReading) : '')
    setNotes(existing?.notes ?? '')
    setPhoto(null)
    setError(null)
  }, [open, existing, plotStatus.suggestedPreviousReading])

  const prevNum = parseFloat(previousReading)
  const curNum = parseFloat(currentReading)
  const unitsConsumed =
    Number.isFinite(prevNum) && Number.isFinite(curNum) ? roundMeterUnits(curNum - prevNum) : null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (isPhotoPending) {
      if (!photo) {
        setError('A meter photo is required')
        return
      }
      try {
        await attachPhoto.mutateAsync({ id: existing!.id, photo })
        onClose()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to attach photo')
      }
      return
    }

    if (!Number.isFinite(prevNum) || !Number.isFinite(curNum)) {
      setError('Enter valid meter readings')
      return
    }
    if (prevNum < 0 || curNum < 0) {
      setError('Readings cannot be negative')
      return
    }
    if (curNum < prevNum) {
      setError('Current reading must be ≥ previous reading')
      return
    }

    try {
      if (existing) {
        await update.mutateAsync({
          id: existing.id,
          body: {
            previousReading: prevNum,
            currentReading: curNum,
            ...(notes && { notes }),
            ...(photo && { photo }),
          },
        })
      } else {
        await save.mutateAsync({
          utilityTypeId: utilityType.id,
          plotId: plotStatus.plotId,
          billingPeriod,
          previousReading: prevNum,
          currentReading: curNum,
          ...(photo && { photo }),
          ...(notes && { notes }),
        })
      }
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save reading')
    }
  }

  const isPending = save.isPending || update.isPending || attachPhoto.isPending

  return (
    <Modal open={open} onClose={onClose} labelledBy="meter-reading-modal-title">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
              {utilityType.name} — {billingPeriod}
            </p>
            <h2 id="meter-reading-modal-title" className="font-serif text-xl text-ink">
              Plot {plotStatus.plotNumber}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <ModalBody className="space-y-4">
          {isReadOnly && (
            <p className="rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-xs text-muted-foreground">
              This reading is {existing.status === 'posted' ? 'posted' : 'pending correction'} and
              is read-only here.
            </p>
          )}
          {isPhotoPending && (
            <p className="rounded border border-border-2 bg-cream-2 px-3 py-2 font-sans text-xs text-muted-foreground">
              Imported from a bulk upload. Attach a meter photo to finish this reading.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Previous reading"
              type="number"
              step="any"
              min={0}
              value={previousReading}
              onChange={(e) => setPreviousReading(e.target.value)}
              disabled={fieldsDisabled}
              required
            />
            <Input
              label="Current reading"
              type="number"
              step="any"
              min={0}
              value={currentReading}
              onChange={(e) => setCurrentReading(e.target.value)}
              disabled={fieldsDisabled}
              required
            />
          </div>

          {unitsConsumed !== null && unitsConsumed >= 0 && (
            <p className="font-sans text-sm text-muted-foreground">
              Units consumed: <span className="font-medium text-ink">{unitsConsumed}</span>
              {utilityType.unitLabel ? ` ${utilityType.unitLabel}` : ''}
            </p>
          )}

          <div className="space-y-1.5">
            <span className="font-sans text-sm font-medium text-ink">
              Meter photo
              {!isPhotoPending && (
                <span className="font-normal text-muted-foreground"> (optional)</span>
              )}
            </span>
            {existing?.photoR2Key && !photo && (
              <a
                href={readingPhotoUrl(existing.id)}
                target="_blank"
                rel="noreferrer"
                className="block font-sans text-xs text-primary underline"
              >
                View current photo
              </a>
            )}
            {!isReadOnly && (
              <>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/heic,image/heif"
                  capture="environment"
                  onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
                  className="block w-full font-sans text-sm text-ink file:mr-3 file:rounded file:border file:border-input file:bg-card file:px-3 file:py-1.5 file:font-sans file:text-sm file:text-ink"
                />
                <p className="font-sans text-xs text-muted-foreground">
                  Opens your camera — take the photo at the meter, right now, rather than picking an
                  old one.
                </p>
              </>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="reading-notes" className="font-sans text-sm font-medium text-ink">
              Notes (optional)
            </label>
            <textarea
              id="reading-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={fieldsDisabled}
              rows={2}
              className="flex w-full rounded border border-input bg-card px-3.5 py-2 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {error && (
            <p className="font-sans text-xs text-destructive" role="alert">
              {error}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {!isReadOnly && (
            <Button type="submit" loading={isPending}>
              {isPhotoPending ? 'Attach photo' : 'Save reading'}
            </Button>
          )}
        </ModalFooter>
      </form>
    </Modal>
  )
}
